import type { Schema } from '@tiptap/pm/model';
import { Fragment, Slice } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

/**
 * Turns pasted plain text into one paragraph per line, so blank lines survive as
 * empty paragraphs. This is the same shape `draftToTiptapHtml` produces and
 * `extractPlainText` reads back, so `a\n\nb` round-trips instead of collapsing.
 *
 * The slice is open at both ends so the first and last pasted lines merge into
 * the paragraph the cursor sits in rather than splitting it.
 */
export function textToParagraphSlice(text: string, schema: Schema): Slice {
  const paragraphs = text
    .split(/\r\n?|\n/)
    .map((line) => schema.nodes.paragraph.create(null, line ? schema.text(line) : null));

  return new Slice(Fragment.fromArray(paragraphs), 1, 1);
}

function containsMention(slice: Slice): boolean {
  let found = false;
  slice.content.descendants((node) => {
    if (node.type.name === 'mention') {
      found = true;
    }
    return !found;
  });
  return found;
}

/**
 * Paste handler for the Vega composer.
 *
 * The composer's schema is plain text plus @-mention chips and hard breaks -- no
 * marks or lists -- and ProseMirror's plain-text parser splits on `/(?:\r\n?|\n)+/`,
 * which collapses runs of newlines and drops blank lines. That destroys the paragraph
 * separation the agent reads, so we take `text/plain` off the clipboard and rebuild
 * the lines ourselves.
 *
 * Three cases are left to Tiptap:
 *
 *  1. Single-line pastes, which have no line structure to protect.
 *  2. ProseMirror-sourced HTML (`data-pm-slice`): the parsed `slice` already
 *     encodes the correct paragraph structure.  Rebuilding from `text/plain`
 *     would introduce spurious empty paragraphs because ProseMirror separates
 *     blocks with `\n\n` in its text serialisation.
 *  3. Pastes whose parsed slice contains an @-mention chip — the chip is worth
 *     more than the blank lines around it.
 */
export function handleVegaPaste(view: EditorView, event: ClipboardEvent, slice: Slice): boolean {
  const text = event.clipboardData?.getData('text/plain');
  if (!text || !/\r|\n/.test(text)) {
    return false;
  }

  const html = event.clipboardData?.getData('text/html');
  if (html && /data-pm-slice/.test(html)) {
    return false;
  }

  if (containsMention(slice)) {
    return false;
  }

  view.dispatch(view.state.tr.replaceSelection(textToParagraphSlice(text, view.state.schema)).scrollIntoView());
  return true;
}
